import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="claim-reexports"
export default class extends Controller {
  static targets = [
    "button",
    "checkbox",
    "selectAll",
    "label",
    "pageSelection",
    "pageSelectionText",
    "pageSelectionLink",
    "clearAllLink"
  ]
  static values = { newUrl: String }

  connect() {
    this.allMatchingSelected = false
    this.allMatchingQuery = null
    this.refresh()
  }

  checkboxTargetConnected() {
    this.refresh()
  }

  checkboxTargetDisconnected() {
    this.refresh()
  }

  selectAllTargetConnected() {
    this.refresh()
  }

  pageSelectionTargetConnected() {
    this.refresh()
  }

  toggleAll(event) {
    this.allMatchingSelected = false
    this.allMatchingQuery = null
    this.checkboxes().forEach((checkbox) => {
      checkbox.checked = event.target.checked
    })
    this.refresh()
  }

  toggleOne() {
    if (this.allMatchingSelected) {
      this.allMatchingSelected = false
      this.allMatchingQuery = null
    }
    this.refresh()
  }

  selectAllMatching(event) {
    event.preventDefault()
    this.allMatchingSelected = true
    this.allMatchingQuery = this.currentFilterQuery()
    this.checkboxes().forEach((checkbox) => {
      checkbox.checked = true
    })
    this.refresh()
  }

  clearSelection(event) {
    event.preventDefault()
    this.allMatchingSelected = false
    this.allMatchingQuery = null
    this.checkboxes().forEach((checkbox) => {
      checkbox.checked = false
    })
    this.refresh()
  }

  refresh() {
    if (this.allMatchingSelected && this.allMatchingQuery !== this.currentFilterQuery()) {
      this.allMatchingSelected = false
      this.allMatchingQuery = null
    }

    const totalMatchingCount = this.totalMatchingCount()
    const selectedCount = this.allMatchingSelected ? totalMatchingCount : this.selectedIds().length

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = selectedCount === 0
      if (this.hasLabelTarget) {
        this.labelTarget.textContent = selectedCount > 0 ? `Export selected claims (${selectedCount})` : "Export selected claims"
      }
    }

    const boxes = this.checkboxes()
    const checkedCount = boxes.filter((checkbox) => checkbox.checked).length

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = boxes.length > 0 && checkedCount === boxes.length
      this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < boxes.length
    }

    if (this.hasPageSelectionTarget) {
      const showBanner = boxes.length > 0 && checkedCount === boxes.length && totalMatchingCount > boxes.length
      this.pageSelectionTarget.classList.toggle("hidden", !showBanner)

      if (this.hasPageSelectionTextTarget && showBanner) {
        if (this.allMatchingSelected) {
          this.pageSelectionTextTarget.textContent = `All ${totalMatchingCount.toLocaleString()} claims matching these filters are selected.`
        } else {
          this.pageSelectionTextTarget.textContent = `All ${boxes.length.toLocaleString()} claims on this page are selected.`
        }
      }

      if (this.hasPageSelectionLinkTarget) {
        this.pageSelectionLinkTarget.classList.toggle("hidden", this.allMatchingSelected)
      }

      if (this.hasClearAllLinkTarget) {
        this.clearAllLinkTarget.classList.toggle("hidden", !this.allMatchingSelected)
      }
    }
  }

  open(event) {
    event.preventDefault()
    const ids = this.selectedIds()
    if ((!this.allMatchingSelected && ids.length === 0) || !this.hasNewUrlValue) return

    const url = new URL(this.newUrlValue, window.location.origin)

    if (this.allMatchingSelected) {
      url.searchParams.set("selection_scope", "all_matching")
      this.currentFilterParams().forEach((value, key) => {
        url.searchParams.append(key, value)
      })
    } else {
      ids.forEach((id) => url.searchParams.append("claim_ids[]", id))
    }

    const drawerEvent = new Event("drawer.open")
    drawerEvent.url = url.toString()
    document.dispatchEvent(drawerEvent)
  }

  selectedIds() {
    return this.checkboxes()
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.dataset.claimId)
      .filter(Boolean)
  }

  checkboxes() {
    return Array.from(this.element.querySelectorAll("input[data-claim-id]"))
  }

  totalMatchingCount() {
    if (!this.hasPageSelectionTarget) return this.checkboxes().length

    return Number.parseInt(this.pageSelectionTarget.dataset.totalCount || "0", 10) || this.checkboxes().length
  }

  currentFilterQuery() {
    return this.currentFilterParams().toString()
  }

  currentFilterParams() {
    const params = new URLSearchParams(window.location.search)
    params.delete("page")
    params.delete("claim_ids[]")
    params.delete("selection_scope")
    return params
  }
}
